import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Avatar } from '@/components/Avatar';
import { AvatarEditor } from '@/components/AvatarEditor';
import { useOutletContext } from 'react-router-dom';
import { FloorTheme } from '@/lib/themes';
import { Loader2, Camera, Shield, Mail, Key, LogOut, Eye, EyeOff, Download, Trash2, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StartPage, normalizeStartPage } from '@/lib/startPage';

const FLOOR_SCORE_THRESHOLDS: Record<number, number> = {
    1: 0,
    2: 50,
    3: 100,
    4: 6200,
    5: 16500,
    6: 60000,
    7: 110000,
    8: 180000,
    9: 280000,
    10: 420000,
};

export function Settings() {
    const [user, setUser] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'profile' | 'account' | 'test-switch'>('profile');
    const [searchParams, setSearchParams] = useSearchParams();

    // Avatar State
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [, setIsUploading] = useState(false);

    // Account State

    const [emailForm, setEmailForm] = useState({ newEmail: '', currentPassword: '' });
    const [startPage, setStartPage] = useState<StartPage>('home');
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [accountMessage, setAccountMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [accountMessageScope, setAccountMessageScope] = useState<'password' | 'email' | 'reset' | null>(null);
    const [testFloor, setTestFloor] = useState(3);
    const [testRole, setTestRole] = useState('User');
    const [testScore, setTestScore] = useState(100);
    const [testSwitchMessage, setTestSwitchMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [isTestSwitching, setIsTestSwitching] = useState(false);
    const [isTestLimitUpdating, setIsTestLimitUpdating] = useState(false);
    const [deletionStep, setDeletionStep] = useState<0 | 1 | 2 | 3 | 4>(0);
    const [deletionCode, setDeletionCode] = useState('');
    const [deletionPassword, setDeletionPassword] = useState('');
    const [deletionVerificationToken, setDeletionVerificationToken] = useState('');
    const [deletionFinalToken, setDeletionFinalToken] = useState('');
    const [deletionEmailMasked, setDeletionEmailMasked] = useState('');
    const [deletionFinalConfirmed, setDeletionFinalConfirmed] = useState(false);
    const [deletionError, setDeletionError] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    // Password Reset UI State
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);

    // Context
    const context = useOutletContext<{ theme: FloorTheme }>();
    const theme = context?.theme || { colors: { primary: '#000', border: '#ddd', card: '#fff', text: '#000' } };

    useEffect(() => {
        fetchUser();
        handleVerification();
    }, []);

    const fetchUser = async () => {
        try {
            const res = await api.get('/users/me');
            const normalizedStartPage = normalizeStartPage(res.data?.start_page);
            const effectiveStartPage = normalizedStartPage === 'salon' && !res.data?.salon_enabled
                ? 'home'
                : normalizedStartPage;
            const nextUser = { ...res.data, start_page: effectiveStartPage };
            setUser(nextUser);
            setStartPage(effectiveStartPage);
            if (nextUser.username === 'TestMaster') {
                setTestFloor(Number(nextUser.floor_level || 3));
                setTestRole(nextUser.role_type || 'User');
                setTestScore(Number(nextUser.smile_score || 0));
            }
            localStorage.setItem('user', JSON.stringify(nextUser));
            window.dispatchEvent(new Event('maison:user-updated'));
        } catch (err) {
            console.error(err);
        }
    };

    const handleVerification = async () => {
        const action = searchParams.get('action');
        const token = searchParams.get('token');

        if (!action || !token) return;

        try {
            if (action === 'verify_email_change') {
                await api.post('/users/me/change-email', { token });
                setMessage({ type: 'success', text: 'Email successfully changed.' });
                await fetchUser();
            } else if (action === 'reset_password') {
                // Show Password Reset Modal or Form
                // For simplicity, we can repurpose the Account tab or show a specific UI.
                // Since this page is protected (usually), but verify link might be clicked on a different device?
                // Actually `Settings` page implies logged in.
                // `reset_password` usually happens when logged out.
                // But if the user is logged in, they can also reset.
                // Let's assume for now we handle it here if logged in.
                // If not logged in, they would be redirected to login.
                // I might need a public `ResetPassword` page for better UX, but user asked for "Settings Page".
                // I will add a logic: if action=reset_password, show a specific modal to enter new password.
                setActiveTab('account');
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.response?.data?.detail || 'Verification failed' });
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onload = () => {
                setSelectedImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveAvatar = async (blob: Blob) => {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', blob, 'avatar.jpg');
        try {
            await api.post('/users/me/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            await fetchUser();
            setMessage({ type: 'success', text: 'Avatar updated successfully.' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to upload avatar.' });
        } finally {
            setSelectedImage(null);
            setIsUploading(false);
        }
    };

    const handlePasswordResetRequest = async () => {
        if (!user?.email) return;
        try {
            await api.post('/auth/request-password-reset', { email: user.email });
            setAccountMessage({ type: 'success', text: 'パスワード再設定のリンクをメールで送信しました。' });
            setAccountMessageScope('password');
        } catch (err) {
            setAccountMessage({ type: 'error', text: '送信に失敗しました。時間をおいて再度お試しください。' });
            setAccountMessageScope('password');
        }
    };

    const handleEmailChangeRequest = async () => {
        try {
            await api.post('/users/me/request-email-change', {
                new_email: emailForm.newEmail,
                current_password: emailForm.currentPassword
            });
            setAccountMessage({ type: 'success', text: '新しいメールアドレス宛に確認リンクを送信しました。' });
            setAccountMessageScope('email');
            setEmailForm({ newEmail: '', currentPassword: '' });
        } catch (err: any) {
            setAccountMessage({ type: 'error', text: err.response?.data?.detail || '変更リクエストに失敗しました。' });
            setAccountMessageScope('email');
        }
    };

    const handleNewPasswordSubmit = async (newPassword: string) => {
        const token = searchParams.get('token');
        try {
            await api.post('/auth/reset-password', { token, new_password: newPassword });
            setAccountMessage({ type: 'success', text: 'パスワードを更新しました。' });
            setAccountMessageScope('reset');
            // clear params
            setSearchParams({});
        } catch (err: any) {
            setAccountMessage({ type: 'error', text: err.response?.data?.detail || 'パスワード更新に失敗しました。' });
            setAccountMessageScope('reset');
        }
    };

    const handleStartPageSave = async () => {
        try {
            await api.patch('/users/me', { start_page: startPage });
            setMessage({ type: 'success', text: '起動時の表示ページを更新しました。' });
            await fetchUser();
        } catch (err: any) {
            setMessage({ type: 'error', text: err.response?.data?.detail || '更新に失敗しました。' });
        }
    };

    const handleTestMasterSwitch = async () => {
        setIsTestSwitching(true);
        setTestSwitchMessage(null);
        try {
            const res = await api.post('/users/me/test-master-mode', {
                floor_level: testFloor,
                role_type: testRole,
                smile_score: testScore
            });
            const nextUser = { ...res.data };
            setUser(nextUser);
            setTestFloor(Number(nextUser.floor_level));
            setTestRole(nextUser.role_type);
            setTestScore(Number(nextUser.smile_score));
            localStorage.setItem('user', JSON.stringify(nextUser));
            window.dispatchEvent(new Event('maison:user-updated'));
            setTestSwitchMessage({
                type: 'success',
                text: `Floor ${nextUser.floor_level}／${nextUser.role_type}／${nextUser.smile_score}ポイントに切り替えました。`
            });
        } catch (err: any) {
            setTestSwitchMessage({
                type: 'error',
                text: err.response?.data?.detail || '切り替えに失敗しました。'
            });
        } finally {
            setIsTestSwitching(false);
        }
    };

    const handleTestMasterLimit = async (kind: 'post' | 'video') => {
        setIsTestLimitUpdating(true);
        setTestSwitchMessage(null);
        try {
            const endpoint = kind === 'post'
                ? '/users/me/test-master-post-remaining'
                : '/users/me/test-master-video-remaining';
            const res = await api.post(endpoint);
            setTestSwitchMessage({
                type: 'success',
                text: kind === 'post'
                    ? `今日の投稿を、あと${res.data.remaining_posts_today}件できる状態にしました。`
                    : `今日の動画投稿を、あと${res.data.remaining_videos_today}件できる状態にしました。`
            });
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setTestSwitchMessage({
                type: 'error',
                text: detail?.message || detail || '投稿回数の準備に失敗しました。'
            });
        } finally {
            setIsTestLimitUpdating(false);
        }
    };

    const resetDeletionModal = () => {
        setDeletionStep(0);
        setDeletionCode('');
        setDeletionPassword('');
        setDeletionVerificationToken('');
        setDeletionFinalToken('');
        setDeletionEmailMasked('');
        setDeletionFinalConfirmed(false);
        setDeletionError('');
    };

    const deletionErrorText = (err: any, fallback: string) => {
        const detail = err.response?.data?.detail;
        return detail?.message || detail || fallback;
    };

    const handleDeletionStart = async () => {
        setIsDeletingAccount(true);
        setDeletionError('');
        try {
            const res = await api.post('/users/me/account-deletion/start');
            setDeletionEmailMasked(res.data.email_masked || '登録メール');
            setDeletionStep(2);
        } catch (err: any) {
            setDeletionError(deletionErrorText(err, '確認コードを送れませんでした。'));
        } finally {
            setIsDeletingAccount(false);
        }
    };

    const handleDeletionCodeVerify = async () => {
        setIsDeletingAccount(true);
        setDeletionError('');
        try {
            const res = await api.post('/users/me/account-deletion/verify-email', { code: deletionCode });
            setDeletionVerificationToken(res.data.verification_token);
            setDeletionStep(3);
        } catch (err: any) {
            setDeletionError(deletionErrorText(err, '確認コードを確認できませんでした。'));
        } finally {
            setIsDeletingAccount(false);
        }
    };

    const handleDeletionPasswordVerify = async () => {
        setIsDeletingAccount(true);
        setDeletionError('');
        try {
            const res = await api.post('/users/me/account-deletion/verify-password', {
                verification_token: deletionVerificationToken,
                password: deletionPassword
            });
            setDeletionFinalToken(res.data.final_token);
            setDeletionPassword('');
            setDeletionStep(4);
        } catch (err: any) {
            setDeletionError(deletionErrorText(err, 'パスワードを確認できませんでした。'));
        } finally {
            setIsDeletingAccount(false);
        }
    };

    const handleDeletionConfirm = async () => {
        if (!deletionFinalConfirmed) return;
        setIsDeletingAccount(true);
        setDeletionError('');
        try {
            await api.post('/users/me/account-deletion/confirm', {
                final_token: deletionFinalToken,
                confirmed: true
            });
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login?deletion_requested=1';
        } catch (err: any) {
            setDeletionError(deletionErrorText(err, 'アカウント削除を確定できませんでした。'));
            setIsDeletingAccount(false);
        }
    };

    if (!user) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin" /></div>;

    const isResettingPassword = searchParams.get('action') === 'reset_password';

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-10">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-serif font-bold mb-2">制御室</h1>
                    <p className="opacity-60 text-sm">アカウントと設定の管理</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b" style={{ borderColor: theme.colors.border, color: user?.floor_level === 2 ? '#f8fafc' : undefined }}>
                <button
                    onClick={() => setActiveTab('profile')}
                    className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'profile' ? 'opacity-100' : 'opacity-50 hover:opacity-80'
                        }`}
                >
                    プロフィール
                    {activeTab === 'profile' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: theme.colors.primary }} />
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('account')}
                    className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'account' ? 'opacity-100' : 'opacity-50 hover:opacity-80'
                        }`}
                >
                    アカウント
                    {activeTab === 'account' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: theme.colors.primary }} />
                    )}
                </button>
                {user?.username === 'TestMaster' && (
                    <button
                        onClick={() => setActiveTab('test-switch')}
                        className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'test-switch' ? 'opacity-100' : 'opacity-50 hover:opacity-80'
                            }`}
                    >
                        テスト切替
                        {activeTab === 'test-switch' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: theme.colors.primary }} />
                        )}
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl border shadow-sm p-8 min-h-[400px] text-slate-900" style={{ borderColor: theme.colors.border }}>

                {/* Profile Tab */}
                {activeTab === 'profile' && !isResettingPassword && (
                    <div className="space-y-8 animate-in fade-in duration-300">
                        {message && (
                            <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-900 border border-green-200' : 'bg-red-50 text-red-900 border border-red-200'
                                }`}>
                                {message.type === 'success' ? <Shield className="w-5 h-5" /> : <LogOut className="w-5 h-5" />}
                                {message.text}
                            </div>
                        )}
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                            <span className="p-2 bg-gray-100 rounded-lg"><Camera className="w-5 h-5" /></span>
                            アバター設定
                        </h3>

                        <div className="flex flex-col md:flex-row items-center gap-10">
                            <div className="relative group">
                                <Avatar
                                    username={user.username}
                                    src={user.avatar_path}
                                    size="2xl"
                                    theme={theme}
                                    className="shadow-xl"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute bottom-0 right-0 p-3 bg-black text-white rounded-full shadow-lg hover:scale-110 transition-transform"
                                >
                                    <Camera className="w-5 h-5" />
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                />
                            </div>

                            <div className="flex-1 space-y-4 text-center md:text-left">
                                <div>
                                    <h4 className="font-bold text-xl">{user.username}</h4>
                                    <p className="opacity-60">Floor {user.floor_level} Resident</p>
                                </div>
                                <p className="text-sm opacity-70 leading-relaxed max-w-sm">
                                    新しいプロフィール画像をアップロードして個性を表現しましょう。<br />
                                    保存する前に画像の切り抜きやサイズ調整が可能です。
                                </p>
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    variant="outline"
                                >
                                    画像を選択
                                </Button>
                            </div>
                        </div>

                        {/* Display Name Edit */}
                        <div className="pt-8 border-t" style={{ borderColor: theme.colors.border }}>
                            <h4 className="font-bold text-lg mb-4">表示名設定</h4>
                            <div className="max-w-md space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium opacity-70">表示名 (Display Name)</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={user.display_name || ''}
                                            onChange={(e) => setUser({ ...user, display_name: e.target.value })}
                                            className="flex-1 p-2 border rounded-lg"
                                            placeholder={user.username}
                                        />
                                        <Button
                                            onClick={async () => {
                                                try {
                                                    await api.patch(`/users/me`, { display_name: user.display_name });
                                                    setMessage({ type: 'success', text: '表示名を更新しました。' });
                                                    await fetchUser();
                                                } catch (e) {
                                                    setMessage({ type: 'error', text: '更新に失敗しました。' });
                                                }
                                            }}
                                            className="whitespace-nowrap bg-black text-white"
                                        >
                                            保存
                                        </Button>
                                    </div>
                                    <p className="text-xs opacity-50">
                                        ※ この名前は「謁見の間」やタイムラインで優先的に表示されます。
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-8 border-t" style={{ borderColor: theme.colors.border }}>
                            <h4 className="font-bold text-lg mb-4">起動時の最初のページ</h4>
                            <div className="max-w-md space-y-3">
                                <label className="text-sm font-medium opacity-70">最初に開くページ</label>
                                <div className="flex gap-2">
                                    <select
                                        value={startPage}
                                        onChange={(e) => setStartPage(e.target.value as StartPage)}
                                        className="flex-1 p-2 border rounded-lg bg-white"
                                    >
                                        <option value="home">マイページ（SNS投稿ページ）</option>
                                        <option value="settings">設定ページ</option>
                                        {user?.salon_enabled && (
                                            <option value="salon">サロンページ</option>
                                        )}
                                    </select>
                                    <Button
                                        onClick={handleStartPageSave}
                                        className="whitespace-nowrap bg-black text-white"
                                    >
                                        保存
                                    </Button>
                                </div>
                                <p className="text-xs opacity-50">
                                    ログイン時・リロード時・アプリ起動時に最初に開くページとして使用されます。
                                </p>
                            </div>
                        </div>
                        <div className="pt-8 border-t" style={{ borderColor: theme.colors.border }}>
                            <h4 className="font-bold text-lg mb-4">ネイティブアプリ</h4>
                            <div className="flex max-w-md flex-col gap-3">
                                <a
                                    href="/downloads/maison-portal-native.apk"
                                    download
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition hover:bg-gray-50"
                                    style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                                >
                                    <Download className="w-4 h-4" />
                                    Android版をダウンロード
                                </a>
                                <a
                                    href="/downloads/maison-portal-native.ipa"
                                    download
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition hover:bg-gray-50"
                                    style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                                >
                                    <Download className="w-4 h-4" />
                                    iPhone／iPad版をダウンロード
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {/* Account Tab */}
                {(activeTab === 'account' || isResettingPassword) && (
                    <div className="space-y-12 animate-in fade-in duration-300">
                        {isResettingPassword ? (
                            <div className="max-w-md mx-auto space-y-4">
                                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                    <span className="p-2 bg-yellow-100 text-yellow-800 rounded-lg"><Key className="w-5 h-5" /></span>
                                    パスワードリセット
                                </h3>
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    const fd = new FormData(e.currentTarget);
                                    const password = fd.get('password') as string;
                                    const confirmPassword = fd.get('confirmPassword') as string;

                                    if (password !== confirmPassword) {
                                        setResetError('パスワードが一致しません。');
                                        return;
                                    }

                                    const hasUpperCase = /[A-Z]/.test(password);
                                    const hasLowerCase = /[a-z]/.test(password);
                                    const hasNumber = /[0-9]/.test(password);

                                    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
                                        setResetError('パスワードは、大文字・小文字・数字をそれぞれ1文字以上含める必要があります。');
                                        return;
                                    }

                                    setResetError(null);
                                    handleNewPasswordSubmit(password);
                                }} className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">新しいパスワード</label>
                                        <div className="relative">
                                            <input
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                required
                                                className="w-full p-2 border rounded-lg pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">パスワード（確認）</label>
                                        <div className="relative">
                                            <input
                                                name="confirmPassword"
                                                type={showConfirmPassword ? "text" : "password"}
                                                required
                                                className="w-full p-2 border rounded-lg pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {resetError && (
                                        <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
                                            {resetError}
                                        </div>
                                    )}

                                    <Button type="submit" className="w-full bg-black text-white">パスワードを更新</Button>
                                    {accountMessage && accountMessageScope === 'reset' && (
                                        <div className={`mt-3 p-3 rounded-lg text-sm ${accountMessage.type === 'success'
                                            ? 'bg-green-50 text-green-900 border border-green-200'
                                            : 'bg-red-50 text-red-900 border border-red-200'}`}>
                                            {accountMessage.text}
                                        </div>
                                    )}
                                </form>
                            </div>
                        ) : (
                            <>
                                {/* Password Reset Request */}
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                        <span className="p-2 bg-gray-100 rounded-lg"><Key className="w-5 h-5" /></span>
                                        パスワード
                                    </h3>
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                                        <div className="text-sm">
                                            <p className="font-medium">パスワード変更リクエスト</p>
                                            <p className="opacity-60">登録済みのメールアドレスにリセット用リンクを送信します。</p>
                                        </div>
                                        <Button onClick={handlePasswordResetRequest} variant="outline">
                                            リンクを送信
                                        </Button>
                                    </div>
                                    {accountMessage && accountMessageScope === 'password' && (
                                        <div className={`p-3 rounded-lg text-sm ${accountMessage.type === 'success'
                                            ? 'bg-green-50 text-green-900 border border-green-200'
                                            : 'bg-red-50 text-red-900 border border-red-200'}`}>
                                            {accountMessage.text}
                                        </div>
                                    )}
                                </div>

                                {/* Email Change */}
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                        <span className="p-2 bg-gray-100 rounded-lg"><Mail className="w-5 h-5" /></span>
                                        メールアドレス
                                    </h3>
                                    <div className="p-6 border rounded-xl space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium opacity-70">現在のEmail</label>
                                                <input
                                                    type="text"
                                                    value={user.email}
                                                    disabled
                                                    className="w-full p-2 bg-gray-100 border rounded-lg text-gray-500"
                                                />
                                            </div>
                                        </div>

                                        <div className="pt-4 border-t">
                                            <h4 className="font-medium text-sm mb-3">メールアドレス変更</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">新しいEmail</label>
                                                    <input
                                                        type="email"
                                                        value={emailForm.newEmail}
                                                        onChange={e => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                                                        className="w-full p-2 border rounded-lg"
                                                        placeholder="new@example.com"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">現在のパスワード</label>
                                                    <input
                                                        type="password"
                                                        value={emailForm.currentPassword}
                                                        onChange={e => setEmailForm({ ...emailForm, currentPassword: e.target.value })}
                                                        className="w-full p-2 border rounded-lg"
                                                        placeholder="本人確認"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <Button onClick={handleEmailChangeRequest} className="bg-black text-white">
                                                    変更リクエスト
                                                </Button>
                                            </div>
                                            {accountMessage && accountMessageScope === 'email' && (
                                                <div className={`mt-3 p-3 rounded-lg text-sm ${accountMessage.type === 'success'
                                                    ? 'bg-green-50 text-green-900 border border-green-200'
                                                    : 'bg-red-50 text-red-900 border border-red-200'}`}>
                                                    {accountMessage.text}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Account deletion */}
                                <div className="space-y-4 pt-6 border-t border-red-200">
                                    <h3 className="font-bold text-lg flex items-center gap-2 text-red-800">
                                        <span className="p-2 bg-red-100 rounded-lg"><Trash2 className="w-5 h-5" /></span>
                                        アカウントを削除する
                                    </h3>
                                    <div className="p-5 rounded-xl border border-red-200 bg-red-50 space-y-3">
                                        <p className="text-sm text-red-950 leading-relaxed">
                                            削除を確定すると、すぐにログアウトし、登録メールアドレスも解除されます。
                                            ただし3日間はデータが残り、以前のIDとパスワードで復元できます。
                                        </p>
                                        <p className="text-xs text-red-800">
                                            復元しても、以前のメールアドレスは戻りません。新しいメールアドレスを登録してください。
                                        </p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => {
                                                resetDeletionModal();
                                                setDeletionStep(1);
                                            }}
                                            className="border-red-500 text-red-700 hover:bg-red-100"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            削除手続きへ進む
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* TestMaster-only Test Switch Tab */}
                {activeTab === 'test-switch' && user.username === 'TestMaster' && !isResettingPassword && (
                    <div className="space-y-8 animate-in fade-in duration-300">
                        <div>
                            <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                                <span className="p-2 bg-violet-100 text-violet-800 rounded-lg"><Shield className="w-5 h-5" /></span>
                                テスト用 Floor・クラス切替
                            </h3>
                            <p className="text-sm leading-relaxed text-slate-600">
                                MaisonPortalの見え方を確認するための、TestMaster専用機能です。
                                好きなFloor、クラス、ポイントを選び、「この設定に切り替える」を押してください。
                            </p>
                        </div>

                        {testSwitchMessage && (
                            <div className={`p-4 rounded-lg ${testSwitchMessage.type === 'success'
                                ? 'bg-green-50 text-green-900 border border-green-200'
                                : 'bg-red-50 text-red-900 border border-red-200'}`}>
                                {testSwitchMessage.text}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="test-master-floor" className="text-sm font-bold">Floor（階層）</label>
                                <select
                                    id="test-master-floor"
                                    value={testFloor}
                                    onChange={(e) => setTestFloor(Number(e.target.value))}
                                    className="w-full p-3 border rounded-lg bg-white"
                                >
                                    {Array.from({ length: 10 }, (_, index) => index + 1).map((floor) => (
                                        <option key={floor} value={floor}>Floor {floor}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-slate-500">Floor 1からFloor 10まで自由に選べます。</p>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="test-master-role" className="text-sm font-bold">クラス（役割）</label>
                                <select
                                    id="test-master-role"
                                    value={testRole}
                                    onChange={(e) => setTestRole(e.target.value)}
                                    className="w-full p-3 border rounded-lg bg-white"
                                >
                                    <option value="User">User（一般ユーザー）</option>
                                    <option value="Famiglia">Famiglia（ファミリア）</option>
                                    <option value="Manager">Manager（マネージャー）</option>
                                    <option value="Maestro">Maestro（マエストロ）</option>
                                </select>
                                <p className="text-xs text-slate-500">安全のため、Admin（管理者）には切り替えられません。</p>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="test-master-score" className="text-sm font-bold">ポイント</label>
                                <input
                                    id="test-master-score"
                                    type="number"
                                    min={-1000000}
                                    max={10000000}
                                    value={testScore}
                                    onChange={(e) => setTestScore(Number(e.target.value))}
                                    className="w-full p-3 border rounded-lg bg-white"
                                />
                                <p className="text-xs text-slate-500">自動昇格の直前など、試したい値を直接入力できます。</p>
                            </div>
                        </div>

                        {testFloor < 10 && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 space-y-3">
                                <div>
                                    <p className="text-sm font-bold text-amber-950">自動昇格テストの準備</p>
                                    <p className="text-sm text-amber-900 mt-1">
                                        Floor {testFloor + 1}への昇格ラインは{FLOOR_SCORE_THRESHOLDS[testFloor + 1].toLocaleString()}ポイントです。
                                        1ポイント手前にしてから肯定的な投稿をすると、投稿の加点で昇格するか確認できます。
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setTestScore(FLOOR_SCORE_THRESHOLDS[testFloor + 1] - 1)}
                                    className="bg-white"
                                >
                                    昇格ラインの1ポイント手前にする
                                </Button>
                            </div>
                        )}

                        {(testRole === 'Manager' || testRole === 'Maestro') && (
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
                                自動昇格を試すときは、クラスをUserまたはFamigliaにしてください。ManagerとMaestroは役職クラスのため、自動昇格の対象外です。
                            </div>
                        )}

                        <div className="rounded-xl border border-violet-200 bg-violet-50 p-5">
                            <p className="text-sm font-bold text-violet-950 mb-1">いま選んでいる設定</p>
                            <p className="text-lg text-violet-950">Floor {testFloor} ／ {testRole} ／ {testScore.toLocaleString()}ポイント</p>
                        </div>

                        <Button
                            onClick={handleTestMasterSwitch}
                            disabled={isTestSwitching}
                            className="w-full md:w-auto bg-black text-white"
                        >
                            {isTestSwitching && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            この設定に切り替える
                        </Button>

                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 space-y-4">
                            <div>
                                <p className="font-bold text-emerald-950">投稿上限テストの準備</p>
                                <p className="text-sm text-emerald-900 mt-1 leading-relaxed">
                                    何十件も投稿しなくても、最後の1件と、上限を超える次の1件だけを試せます。
                                    この特権はFloorやクラスを切り替えても失われません。
                                </p>
                            </div>
                            <div className="flex flex-col md:flex-row gap-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={isTestLimitUpdating}
                                    onClick={() => handleTestMasterLimit('post')}
                                    className="bg-white border-emerald-400"
                                >
                                    投稿回数を残り一回にする
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={isTestLimitUpdating}
                                    onClick={() => handleTestMasterLimit('video')}
                                    className="bg-white border-emerald-400"
                                >
                                    動画の投稿回数を残り一回にする
                                </Button>
                            </div>
                            <p className="text-xs text-emerald-800">
                                動画投稿テストはFloor 7以上にしてから行ってください。Floor 6以下では動画機能そのものがまだ開放されません。
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {deletionStep > 0 && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4">
                    <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl relative text-slate-900">
                        <button
                            type="button"
                            aria-label="閉じる"
                            disabled={isDeletingAccount}
                            onClick={resetDeletionModal}
                            className="absolute right-4 top-4 text-slate-400 hover:text-slate-700 disabled:opacity-40"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="p-3 rounded-full bg-red-100 text-red-700"><AlertTriangle className="w-6 h-6" /></span>
                            <div>
                                <p className="text-xs font-bold text-red-700">確認 {deletionStep} / 4</p>
                                <h2 className="text-xl font-bold">アカウント削除</h2>
                            </div>
                        </div>

                        {deletionStep === 1 && (
                            <div className="space-y-5">
                                <div className="space-y-3 text-sm leading-relaxed">
                                    <p>本当に削除手続きを始めますか？ このボタンだけでは、まだ削除されません。</p>
                                    <ul className="list-disc pl-5 space-y-2 text-slate-700">
                                        <li>次に、登録メールへ届く6桁コードを確認します。</li>
                                        <li>そのあと、現在のパスワードを確認します。</li>
                                        <li>最後に、もう一度だけ本当に削除するか確認します。</li>
                                    </ul>
                                </div>
                                <Button type="button" disabled={isDeletingAccount} onClick={handleDeletionStart} className="w-full bg-red-700 text-white hover:bg-red-800">
                                    {isDeletingAccount && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    確認コードを送って次へ
                                </Button>
                            </div>
                        )}

                        {deletionStep === 2 && (
                            <div className="space-y-5">
                                <p className="text-sm leading-relaxed">
                                    <strong>{deletionEmailMasked}</strong> に届いた6桁の確認コードを入力してください。
                                </p>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    value={deletionCode}
                                    onChange={(event) => setDeletionCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="w-full rounded-lg border p-3 text-center text-2xl tracking-[0.35em]"
                                    placeholder="000000"
                                />
                                <Button type="button" disabled={isDeletingAccount || deletionCode.length !== 6} onClick={handleDeletionCodeVerify} className="w-full bg-red-700 text-white hover:bg-red-800">
                                    {isDeletingAccount && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    メールを確認しました
                                </Button>
                            </div>
                        )}

                        {deletionStep === 3 && (
                            <div className="space-y-5">
                                <p className="text-sm">本人であることを確かめるため、現在のパスワードを入力してください。</p>
                                <input
                                    type="password"
                                    autoComplete="current-password"
                                    value={deletionPassword}
                                    onChange={(event) => setDeletionPassword(event.target.value)}
                                    className="w-full rounded-lg border p-3"
                                    placeholder="現在のパスワード"
                                />
                                <Button type="button" disabled={isDeletingAccount || !deletionPassword} onClick={handleDeletionPasswordVerify} className="w-full bg-red-700 text-white hover:bg-red-800">
                                    {isDeletingAccount && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    パスワードを確認して次へ
                                </Button>
                            </div>
                        )}

                        {deletionStep === 4 && (
                            <div className="space-y-5">
                                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-950 leading-relaxed">
                                    <p className="font-bold mb-2">これが最後の確認です。</p>
                                    <p>確定するとすぐにログアウトし、メールアドレスは解除されます。3日以内なら、以前のIDとパスワードで復元できます。</p>
                                </div>
                                <label className="flex items-start gap-3 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={deletionFinalConfirmed}
                                        onChange={(event) => setDeletionFinalConfirmed(event.target.checked)}
                                        className="mt-1 h-4 w-4"
                                    />
                                    <span>説明を読み、アカウント削除を確定することに同意します。</span>
                                </label>
                                <Button type="button" disabled={isDeletingAccount || !deletionFinalConfirmed} onClick={handleDeletionConfirm} className="w-full bg-red-700 text-white hover:bg-red-800">
                                    {isDeletingAccount && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    アカウント削除を確定する
                                </Button>
                            </div>
                        )}

                        {deletionError && (
                            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                {deletionError}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Avatar Editor Modal */}
            {selectedImage && (
                <AvatarEditor
                    imageSrc={selectedImage}
                    onCancel={() => setSelectedImage(null)}
                    onSave={handleSaveAvatar}
                />
            )}
        </div>
    );
}
